import {ArrowLeft, CircleAlert, Clock3, Download, Film, RefreshCw, RotateCw, Trash2} from "lucide-react";
import {useCallback, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import type {TaskDetail} from "@book-video-studio/contracts";
import {deleteTasks, getResultDownloadUrl, getTask, trashTasks} from "../api";
import {ConfirmDialog} from "../components/ConfirmDialog";
import {TaskStatus} from "../components/TaskStatus";
import {isActiveStatus, phaseText} from "../status";

const timeText = (value: string): string => new Intl.DateTimeFormat("zh-CN", {month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit"}).format(new Date(value));

export const TaskDetailPage = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const load = useCallback(async () => {
    if (!id) return;
    try { setTask(await getTask(id)); setError(null); } catch (reason) { setError(reason instanceof Error ? reason.message : "任务加载失败"); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!task || !isActiveStatus(task.status)) return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load, task]);
  const downloadResult = async (): Promise<void> => {
    if (!task || isDownloading) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const link = document.createElement("a");
      link.href = await getResultDownloadUrl(task.id);
      link.download = "final.mp4";
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
      }
    } catch (reason) {
      setDownloadError(reason instanceof Error ? reason.message : "下载地址获取失败");
    } finally {
      setIsDownloading(false);
    }
  };
  const removeTask = async (): Promise<void> => {
    if (!task || isRemoving) return;
    setIsRemoving(true);
    setDownloadError(null);
    try {
      if (task.trashedAt) await deleteTasks([task.id]);
      else await trashTasks([task.id]);
      navigate("/trash");
    } catch (reason) {
      setDownloadError(reason instanceof Error ? reason.message : "操作失败，请稍后重试");
      setConfirmOpen(false);
    } finally {
      setIsRemoving(false);
    }
  };
  if (error) return <div className="empty-state"><CircleAlert size={36} /><h2>{error}</h2><button className="button button-secondary" onClick={() => void load()}><RotateCw size={18} />重试</button></div>;
  if (!task) return <div className="empty-state">正在读取任务……</div>;
  const images = task.assets.filter((asset) => asset.kind === "image");
  return (
    <div className="page-grid detail-page">
      <aside className="process-rail"><span>03</span><b>任务详情</b><p>任务编号<br /><code>{task.id.slice(0, 13)}</code></p></aside>
      <section className="page-content">
        <header className="page-heading"><div><Link className="back-link" to={task.trashedAt ? "/trash" : "/tasks"}><ArrowLeft size={17} />{task.trashedAt ? "回收站" : "任务列表"}</Link><h1>{task.title || `图片轮播 ${task.id.slice(0, 8)}`}</h1></div><div className="heading-actions"><button className="icon-button" type="button" onClick={() => void load()} aria-label="刷新"><RefreshCw size={19} /></button><button className={`button ${task.trashedAt ? "button-danger" : "button-ghost"}`} type="button" onClick={() => setConfirmOpen(true)} disabled={isRemoving}><Trash2 size={18} />{task.trashedAt ? "彻底删除" : "移入回收站"}</button></div></header>
        <div className="detail-status-panel">
          <div><TaskStatus status={task.status} /><strong>{task.progress}%</strong></div>
          <div className="large-progress"><i style={{width: `${task.progress}%`}} /></div>
          <p>{phaseText(task.phase)}</p>
        </div>
        {task.errorMessage ? <p className="notice notice-error"><CircleAlert size={19} />{task.errorMessage}</p> : null}
        {downloadError ? <p className="notice notice-error"><CircleAlert size={19} />{downloadError}</p> : null}
        {task.resultUrl ? (
          <section className="result-panel"><div className="section-heading"><div><Film size={22} /><span><b>成片</b><small>1080 × 1440 · 3:4</small></span></div><button className="button button-secondary" type="button" disabled={isDownloading} onClick={() => void downloadResult()}><Download size={18} />{isDownloading ? "准备下载…" : "下载"}</button></div><video src={task.resultUrl} controls playsInline preload="metadata" /></section>
        ) : null}
        <div className="detail-facts"><div><small>图片</small><b>{images.length} 张</b></div><div><small>创建时间</small><b>{timeText(task.createdAt)}</b></div><div><small>工作流</small><b>图片音频轮播 V1</b></div></div>
        <section className="events"><div className="section-heading"><div><Clock3 size={22} /><span><b>必要记录</b><small>只保存状态变化、警告和失败</small></span></div></div>{task.events.length === 0 ? <p className="image-empty">暂无记录</p> : <ol>{task.events.map((event) => <li key={event.id}><i /><span><b>{event.message}</b><small>{timeText(event.createdAt)}</small></span></li>)}</ol>}</section>
      </section>
      <ConfirmDialog
        open={confirmOpen}
        title={task.trashedAt ? "彻底删除这个任务？" : "将这个任务移入回收站？"}
        description={task.trashedAt
          ? "任务素材和成片将从 COS 永久删除，数据库记录也会删除。此操作不可恢复。"
          : "任务将从任务列表隐藏，本地工作目录会由 Worker 清理；COS 素材和成片仍会保留。"}
        confirmLabel={task.trashedAt ? "确认永久删除" : "确认移入回收站"}
        destructive={Boolean(task.trashedAt)}
        busy={isRemoving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void removeTask()}
      />
    </div>
  );
};
